import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { SETUP_TOUR_STEPS, type SetupTourStep } from './setupTourSteps';
import {
  trackSetupTourStarted,
  trackSetupTourStepViewed,
  trackSetupTourCompleted,
  trackSetupTourSkipped,
} from '@/lib/analytics';

export type SetupTourStatus = 'idle' | 'active' | 'completed';

const SETUP_TOUR_STORAGE_KEY = 'prepframe_setup_tour';

interface SetupTourState {
  completed?: boolean;
  skipped?: boolean;
}

function loadSetupTourState(): SetupTourState {
  try {
    const raw = localStorage.getItem(SETUP_TOUR_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSetupTourState(state: SetupTourState) {
  localStorage.setItem(SETUP_TOUR_STORAGE_KEY, JSON.stringify(state));
}

interface SetupTourContextType {
  status: SetupTourStatus;
  currentStepIndex: number;
  currentStep: SetupTourStep | null;
  totalSteps: number;

  startSetupTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipSetupTour: () => void;
  completeSetupTour: () => void;
  resetSetupTour: () => void;
  /** Returns true if the setup tour was already completed or skipped */
  isSetupTourDismissed: () => boolean;
}

const SetupTourContext = createContext<SetupTourContextType>({
  status: 'idle',
  currentStepIndex: 0,
  currentStep: null,
  totalSteps: SETUP_TOUR_STEPS.length,
  startSetupTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipSetupTour: () => {},
  completeSetupTour: () => {},
  resetSetupTour: () => {},
  isSetupTourDismissed: () => true,
});

export function SetupTourProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SetupTourStatus>('idle');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const isSetupTourDismissed = useCallback(() => {
    const state = loadSetupTourState();
    return !!(state.completed || state.skipped);
  }, []);

  const startSetupTour = useCallback(() => {
    const state = loadSetupTourState();
    if (state.completed || state.skipped) return;

    setCurrentStepIndex(0);
    setStatus('active');
    trackSetupTourStarted();
    trackSetupTourStepViewed({ step_id: SETUP_TOUR_STEPS[0].id, step_index: 0 });
  }, []);

  const nextStep = useCallback(() => {
    const nextIdx = currentStepIndex + 1;
    if (nextIdx >= SETUP_TOUR_STEPS.length) {
      setStatus('completed');
      return;
    }
    setCurrentStepIndex(nextIdx);
    trackSetupTourStepViewed({ step_id: SETUP_TOUR_STEPS[nextIdx].id, step_index: nextIdx });
  }, [currentStepIndex]);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const skipSetupTour = useCallback(() => {
    setStatus('idle');
    saveSetupTourState({ skipped: true });
    trackSetupTourSkipped({ last_step_index: currentStepIndex });
  }, [currentStepIndex]);

  const completeSetupTour = useCallback(() => {
    setStatus('idle');
    saveSetupTourState({ completed: true });
    trackSetupTourCompleted();
  }, []);

  const resetSetupTour = useCallback(() => {
    localStorage.removeItem(SETUP_TOUR_STORAGE_KEY);
    setCurrentStepIndex(0);
    setStatus('idle');
  }, []);

  const currentStep = status === 'active' ? SETUP_TOUR_STEPS[currentStepIndex] ?? null : null;

  return (
    <SetupTourContext.Provider
      value={{
        status,
        currentStepIndex,
        currentStep,
        totalSteps: SETUP_TOUR_STEPS.length,
        startSetupTour,
        nextStep,
        prevStep,
        skipSetupTour,
        completeSetupTour,
        resetSetupTour,
        isSetupTourDismissed,
      }}
    >
      {children}
    </SetupTourContext.Provider>
  );
}

export function useSetupTour() {
  return useContext(SetupTourContext);
}
